import { ALL_COLUMNS_ID } from "../columnFilters/columnDefinition";
import type {
  ColumnFilter,
  ColumnFiltersState,
  FilterCondition,
} from "../columnFilters/state";

export interface HighlightRange {
  start: number;
  end: number;
  colorIndex: number;
}

/** Conditions that produce visible text highlights. */
const HIGHLIGHTABLE_CONDITIONS = new Set<FilterCondition>([
  "contains",
  "starts_with",
  "ends_with",
  "is_exactly",
  "matches_regex",
]);

/**
 * Pre-computed stage data for highlight matching.
 * Avoids re-lowercasing values and recompiling regexes per cell.
 */
export interface PreparedStage {
  condition: FilterCondition;
  value: string;
  lowerValue: string;
  regex: RegExp | null;
  colorIndex: number;
}

/**
 * Assigns a global color index to each (columnId, stageIndex) pair.
 *
 * Every stage gets a color index — not just highlightable ones — so that
 * FilterBar pills for datetime/numeric conditions also display a color.
 * The highlight codepath independently checks HIGHLIGHTABLE_CONDITIONS,
 * so non-highlightable stages still produce no cell highlights.
 *
 * Preserves color indices across rebuilds: keys that were present in
 * `previous` keep their index, so adding or removing a filter never
 * reshuffles the colors of unrelated existing filters. New keys take the
 * lowest unused index (filling gaps left by removed filters). When called
 * without a previous map, indices are assigned in (sorted columnId, stage)
 * order so the first render is deterministic.
 *
 * Returns a map from `${columnId}:${stageIndex}` to a color index.
 */
export function buildColorMap(
  filters: Record<string, ColumnFilter>,
  previous: Map<string, number> = new Map(),
): Map<string, number> {
  const next = new Map<string, number>();
  const used = new Set<number>();
  const newKeys: string[] = [];

  // Sort by column ID so first-render ordering is stable and matches the
  // sorted pill display order in ActiveFilterPills.
  const sortedEntries = Object.entries(filters).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  for (const [columnId, filter] of sortedEntries) {
    for (let i = 0; i < filter.stages.length; i++) {
      const key = `${columnId}:${i}`;
      const existing = previous.get(key);
      if (existing !== undefined) {
        next.set(key, existing);
        used.add(existing);
      } else {
        newKeys.push(key);
      }
    }
  }

  let nextIndex = 0;
  for (const key of newKeys) {
    while (used.has(nextIndex)) {
      nextIndex++;
    }
    next.set(key, nextIndex);
    used.add(nextIndex);
    nextIndex++;
  }

  return next;
}

/**
 * Pre-computes lowercased values and compiled regexes for all highlightable
 * stages in a single column filter.
 */
function prepareStages(
  columnId: string,
  filter: ColumnFilter,
  colorMap: Map<string, number>,
): PreparedStage[] {
  const prepared: PreparedStage[] = [];
  for (let i = 0; i < filter.stages.length; i++) {
    const stage = filter.stages[i];
    if (!HIGHLIGHTABLE_CONDITIONS.has(stage.condition) || stage.value === "") {
      continue;
    }
    const colorIndex = colorMap.get(`${columnId}:${i}`) ?? 0;
    let regex: RegExp | null = null;
    if (stage.condition === "matches_regex") {
      try {
        // "gi" flags: "g" (global) is required for exec() iteration to find all
        // match positions, and "i" for case-insensitive matching. This differs from
        // LogSearchEngine which uses "i" only — test() just checks existence and
        // does not need the global flag.
        regex = new RegExp(stage.value, "gi");
      } catch {
        // Invalid regex — skip this stage
        continue;
      }
    }
    prepared.push({
      condition: stage.condition,
      value: stage.value,
      lowerValue: stage.value.toLowerCase(),
      regex,
      colorIndex,
    });
  }
  return prepared;
}

/**
 * A map from column ID to its pre-computed highlight stages. Build once per
 * filter change (via `prepareFilters`) and pass to every `computeHighlights`
 * call, avoiding redundant regex compilation and string lowercasing per cell.
 */
export type PreparedFilters = Map<string, PreparedStage[]>;

/**
 * Pre-computes highlight stages for all filters. Call once per filter change
 * (typically inside a useMemo) and pass the result to `computeHighlights`.
 */
export function prepareFilters(
  filtersState: ColumnFiltersState,
  colorMap: Map<string, number>,
): PreparedFilters {
  const prepared: PreparedFilters = new Map();
  for (const [columnId, filter] of Object.entries(filtersState.filters)) {
    const stages = prepareStages(columnId, filter, colorMap);
    if (stages.length > 0) {
      prepared.set(columnId, stages);
    }
  }
  return prepared;
}

/**
 * Finds all non-overlapping highlight ranges in `text` for the given column.
 *
 * Checks both column-specific filters and "All columns" filters.
 * Merges overlapping ranges, preferring the earlier (lower colorIndex) match.
 *
 * Accepts a `PreparedFilters` map (from `prepareFilters`) so that regex
 * compilation and string lowercasing happen once per filter change, not per cell.
 */
export function computeHighlights(
  text: string,
  columnId: string,
  preparedFilters: PreparedFilters,
): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  const lower = text.toLowerCase();

  // Collect ranges from column-specific filter
  const columnStages = preparedFilters.get(columnId);
  if (columnStages) {
    for (const stage of columnStages) {
      findMatchRanges(text, lower, stage, ranges);
    }
  }

  // Collect ranges from "All columns" filter
  const allStages = preparedFilters.get(ALL_COLUMNS_ID);
  if (allStages) {
    for (const stage of allStages) {
      findMatchRanges(text, lower, stage, ranges);
    }
  }

  if (ranges.length === 0) {
    return ranges;
  }

  return mergeRanges(ranges);
}

/**
 * Appends highlight ranges for a single prepared stage's matches within `text`.
 */
function findMatchRanges(
  text: string,
  lower: string,
  stage: PreparedStage,
  out: HighlightRange[],
): void {
  const { condition, lowerValue, colorIndex } = stage;

  switch (condition) {
    case "contains": {
      let pos = 0;
      while (pos < lower.length) {
        const idx = lower.indexOf(lowerValue, pos);
        if (idx === -1) {
          break;
        }
        out.push({ start: idx, end: idx + stage.value.length, colorIndex });
        pos = idx + lowerValue.length;
      }
      break;
    }

    case "starts_with": {
      if (lower.startsWith(lowerValue)) {
        out.push({ start: 0, end: stage.value.length, colorIndex });
      }
      break;
    }

    case "ends_with": {
      if (lower.endsWith(lowerValue)) {
        out.push({
          start: text.length - stage.value.length,
          end: text.length,
          colorIndex,
        });
      }
      break;
    }

    case "is_exactly": {
      if (lower === lowerValue) {
        out.push({ start: 0, end: text.length, colorIndex });
      }
      break;
    }

    case "matches_regex": {
      if (stage.regex === null) {
        break;
      }
      // Reset lastIndex for stateful regex with "g" flag
      stage.regex.lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = stage.regex.exec(text)) !== null) {
        if (match[0].length === 0) {
          // Avoid infinite loop on zero-length matches
          stage.regex.lastIndex++;
          continue;
        }
        out.push({
          start: match.index,
          end: match.index + match[0].length,
          colorIndex,
        });
      }
      break;
    }
  }
}

/**
 * Merges overlapping ranges.
 * When ranges overlap, the one with the lower colorIndex wins.
 * Returns a sorted, non-overlapping array.
 */
function mergeRanges(ranges: HighlightRange[]): HighlightRange[] {
  if (ranges.length === 0) {
    return [];
  }

  // Sort by start position, then by colorIndex (lower wins ties)
  const sorted = ranges.slice().sort((a, b) => {
    if (a.start !== b.start) {
      return a.start - b.start;
    }
    return a.colorIndex - b.colorIndex;
  });

  const merged: HighlightRange[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    if (current.start >= last.end) {
      // No overlap
      merged.push(current);
    } else if (current.end <= last.end) {
      // Fully contained — skip (earlier colorIndex already wins)
    } else {
      // Partial overlap — trim current to start after last ends
      merged.push({
        start: last.end,
        end: current.end,
        colorIndex: current.colorIndex,
      });
    }
  }

  return merged;
}
